import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CELL_DOOR_OFF, ownerHubDoorOrigin, ownerHubToken } from '@/lib/cell/ownerHub';
import { useActiveLocationId } from '@/lib/locationContext';
import { useFeature } from '@/lib/useFeature';

/**
 * CHECK MY LISTING — what the outside world sees when someone looks for you.
 *
 * BUILT TO THE APPROVED DESIGN, whose own breadcrumb names this address:
 *   design/pro-check-my-listing.html   — Erica's report, and the shape of this page
 *   design/pro-check-shelby.html       — the second worked example
 *   design/floor-basics.artifact.html  — the nine checks, proven across all fourteen Sharon pros
 *
 * THE PLACEMENT IS THE DESIGN'S, NOT A CHOICE MADE HERE. "The checks live in two places only: a
 * pro's own report in their hub, and a section on that one tenant's page under Intelligence."
 * A first attempt put them on the Reviews page instead — findable only by opening Grow, then
 * Insights, then a card labelled Reviews — and that card is removed in the same change.
 *
 * WHOSE PAGE THIS IS — AND WHO ELSE CAN SEE IT (HUB-064, fix-m7, 2026-10-11). This page used to
 * promise "Nobody at the studio sees this but you" and "Nothing here is shared with the studio",
 * but the door (image-os-web src/routes/pro.checks.ts, `view_pro` → app_view_pro) answers the
 * pro's owner the same report through View their hub, and the same facts are on the owner's side
 * under Intelligence. The page now says exactly that. The difference is not the
 * data — it is that here every finding is theirs to act on and carries the how-to, while there it
 * is context for a conversation.
 *
 * NOTHING IS SCORED. There is no grade, no percentage out of a hundred and no ranking against
 * the rest of the floor — the design ruled that out by name: "a grid comparing fourteen pros side
 * by side is a ranking with the labels removed."
 */

import { viewAsProId } from '@/lib/viewAs';
// CELL (M28): the Owner Hub door for this build — doorOrigin() in production (lib/hosts.ts), the AWS staging host on
// the cell build; null there = not on the AWS staging build yet, and the door helper below says so (CELL_DOOR_OFF).
const DOOR = ownerHubDoorOrigin();

type Check = { key: string; label: string; state: 'ok' | 'no' | 'unknown'; detail: string };
type Payload = {
  ok: boolean; error?: string;
  checks: Check[];
  failing: number;
  inPlace: number;
  ofKnown: number;
  checkedAt: string | null;
  /** When we last LOOKED, whatever we found. Null = never. */
  lookedAt: string | null;
  sweepState: string;
  sweepNote: string;
  businessName: string;
  listingAddress: string;
  listingName: string;
  bookingUrl: string;
  /** True under an operator's View-their-hub lens: read only, never a run. */
  viewing: boolean;
  /** What "check again" did: ran | recent (looked within the hour) | no_name | unavailable | failed | null. */
  ran: 'ran' | 'recent' | 'no_name' | 'unavailable' | 'failed' | null;
};

/**
 * The door is asked for the CALLER's own checks. Under an operator's View-their-hub lens the
 * lens's identity rides along as view_pro (the #99 lesson: every door a lensed surface calls must
 * carry the lens, or it shows the viewer's own page); `run` asks the door to sweep the pro's
 * listing NOW, which only the pro themselves may do.
 */
async function fetchChecks(locationId: string, opts: { run?: boolean } = {}): Promise<Payload> {
  if (!DOOR) throw new Error(CELL_DOOR_OFF);
  // CELL (M28): this tab's own token — Supabase's in production, Cognito's access token on the cell build (lib/cell/ownerHub.ts).
  const token = await ownerHubToken();
  if (!token) throw new Error('Sign in again — no session.');
  const q = new URLSearchParams({ location_id: locationId });
  const viewing = viewAsProId();
  if (viewing) q.set('view_pro', viewing);
  else if (opts.run) q.set('run', '1');
  const res = await fetch(`${DOOR}/pro/checks?${q.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const body = (await res.json().catch(() => null)) as Payload | null;
  if (!res.ok || !body?.ok) throw new Error(body?.error ?? 'Could not check your listing right now.');
  return body;
}

/**
 * The design's three marks. An unknown gets a MIDDOT and grey — never a cross, because the state
 * exists precisely so a check we could not run is not read as something they failed.
 */
const MARK = { ok: '✓', no: '✕', unknown: '·' } as const;
const MARK_CLASS = {
  ok: 'text-green-700',
  no: 'text-image-pink',
  unknown: 'text-image-slate',
} as const;

function CheckRow({ c }: { c: Check }) {
  return (
    <li className="flex gap-3 py-3 border-b border-image-pale last:border-b-0 items-start">
      <span aria-hidden="true" className={`w-4 shrink-0 leading-6 ${MARK_CLASS[c.state]}`}>
        {MARK[c.state]}
      </span>
      <span className={c.state === 'unknown' ? 'text-image-slate' : ''}>
        <b className="block text-sm font-semibold text-image-black">{c.label}</b>
        <small className="block text-xs text-image-charcoal mt-1 leading-relaxed">{c.detail}</small>
      </span>
    </li>
  );
}

export default function CheckMyListing() {
  const { activeLocationId } = useActiveLocationId();
  // Rides the Grow key: this room is a tool inside the Grow wing, not a separate pilot.
  const feature = useFeature('grow');

  const qc = useQueryClient();
  const [running, setRunning] = useState(false);
  const { data, isLoading, error } = useQuery<Payload>({
    queryKey: ['pro-check-my-listing', activeLocationId],
    enabled: !!activeLocationId && feature.enabled,
    queryFn: () => fetchChecks(activeLocationId!),
  });
  /**
   * CHECK AGAIN NOW actually checks (IOSW-775). The old button refetched the door, and the door
   * re-read the row the owner's last sweep had stored — a pro pressing it could never make a
   * search happen. This asks the door to sweep their listing now and lands the answer in the same
   * cache the page reads.
   */
  const runNow = async () => {
    if (!activeLocationId) return;
    setRunning(true);
    try {
      await qc.fetchQuery({
        queryKey: ['pro-check-my-listing', activeLocationId],
        queryFn: () => fetchChecks(activeLocationId, { run: true }),
        staleTime: 0,
      });
    } finally { setRunning(false); }
  };

  if (!feature.loading && !feature.enabled) {
    return (
      <div className="card text-sm text-image-charcoal">
        This section isn&rsquo;t switched on for your studio yet.
      </div>
    );
  }
  if (feature.loading || isLoading) return <div className="text-image-slate text-sm">Checking&hellip;</div>;
  if (error || !data) {
    return (
      <div className="card text-sm text-image-charcoal">
        We couldn&rsquo;t run the checks just now — try again in a minute.
      </div>
    );
  }

  const todo = data.checks.filter((c) => c.state === 'no');
  const working = data.checks.filter((c) => c.state === 'ok');
  const unknown = data.checks.filter((c) => c.state === 'unknown');

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-2xl font-bold text-image-black leading-tight">
          What the outside world sees when someone looks for you.
        </h1>
        <p className="text-sm text-image-charcoal mt-2 leading-relaxed">
          We check what&rsquo;s public — nothing magical, nothing you couldn&rsquo;t look up
          yourself. We just did it all at once.{' '}
          <b className="text-image-black">Your studio&rsquo;s owner can see this same report.</b>
        </p>
        <p className="text-xs text-image-slate mt-2">
          {[data.businessName, data.checkedAt ? `checked ${data.checkedAt.slice(0, 10)}` : 'not checked yet']
            .filter(Boolean).join(' · ')}
        </p>
      </div>

      {/* THE SHORT VERSION — what is working first, then the one thing that matters. The design
          leads with the good news on both worked examples, and says why: the list of problems is
          short and the reason is narrow. */}
      <div className="card">
        <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-image-charcoal mb-2">
          The short version
        </p>
        <p className="text-sm text-image-charcoal leading-relaxed">
          {/**
            * NOTHING CHECKED IS NOT ALL CLEAR. Seen on the live page the moment it rendered for a
            * pro with no business name on file: every check was an honest "we couldn't", and the
            * summary read "Everything we can check is in place. Nothing here needs you." Nothing
            * was in place; nothing had been looked at. An all-clear over an empty check set is
            * the most confident kind of wrong.
            */}
          {data.ofKnown === 0
            ? 'We could not check anything yet — see below for why, and it is not something you did.'
            : todo.length === 0
              ? 'Everything we can check is in place. Nothing here needs you.'
              : (
              <>
                <b className="text-image-black">
                  {data.inPlace} of {data.ofKnown} in place.
                </b>{' '}
                {todo.length === 1
                  ? 'One thing is missing, and it is yours to fix — or not, entirely your call.'
                  : `${todo.length} things are missing. Most of what's below is free and takes minutes.`}
                </>
              )}
        </p>
      </div>

      {todo.length > 0 && (
        <section>
          <h2 className="text-[11px] font-bold uppercase tracking-[0.15em] text-image-charcoal px-1 mb-2">
            What to do about it
          </h2>
          <div className="card">
            <p className="text-xs text-image-slate mb-1">
              In order of what would move the needle most. All of it is yours to do — or not.
            </p>
            <ul>{todo.map((c) => <CheckRow key={c.key} c={c} />)}</ul>
          </div>
        </section>
      )}

      {working.length > 0 && (
        <section>
          <h2 className="text-[11px] font-bold uppercase tracking-[0.15em] text-image-charcoal px-1 mb-2">
            What&rsquo;s already working
          </h2>
          <div className="card">
            <ul>{working.map((c) => <CheckRow key={c.key} c={c} />)}</ul>
          </div>
        </section>
      )}

      {unknown.length > 0 && (
        <section>
          <h2 className="text-[11px] font-bold uppercase tracking-[0.15em] text-image-charcoal px-1 mb-2">
            What we couldn&rsquo;t check
          </h2>
          <div className="card">
            {/* NEVER COUNTED AS A FAIL, and the page says whose side the gap is on. The design is
                explicit: an Instagram check failing tells us about OUR credential, not their
                account. */}
            <p className="text-xs text-image-slate mb-1">
              These aren&rsquo;t findings and they aren&rsquo;t yours to fix — they are the things
              our own checks couldn&rsquo;t answer.
            </p>
            <ul>{unknown.map((c) => <CheckRow key={c.key} c={c} />)}</ul>
          </div>
        </section>
      )}

      <div className="card">
        <p className="text-sm text-image-charcoal leading-relaxed">
          <b className="text-image-black">Once those are done, check again.</b>{' '}
          The only honest way to know any of it worked is to run the same checks and see whether
          the answers changed.
        </p>
        {data.viewing ? (
          <p className="text-xs text-image-slate mt-3">
            You are viewing this as the studio. Only the pro can run their own checks.
          </p>
        ) : (
          <>
            <button
              type="button"
              className="btn-primary mt-3"
              disabled={running}
              onClick={() => { void runNow(); }}
            >
              {running ? 'Checking…' : 'Check again now'}
            </button>
            {data.ran && (
              <p className="text-xs text-image-slate mt-2">
                {{
                  ran: 'Checked just now — the answers above are today’s.',
                  recent: 'We looked within the last hour, so the answers above are current.',
                  no_name: 'We hold no business name for you, so there is nothing to search yet.',
                  unavailable: 'Google is not answering right now — try again a little later.',
                  failed: 'The check could not finish — try again in a minute.',
                }[data.ran]}
              </p>
            )}
          </>
        )}
      </div>

      <p className="text-xs text-image-slate leading-relaxed px-1">
        <b>This is yours to act on.</b> We ran public searches about your business and wrote down
        the answers. Your studio&rsquo;s owner can open the same report from their side; only you can
        run the checks, and nothing is scored.
      </p>
    </div>
  );
}
